// WHICH PROJECT YOU ARE WORKING IN (nav v2): the scoping model, with no React and no requests, so it runs under node
// (test/project-scope/model.test.mjs).
//
// A PROJECT IS THE WORKSPACE YOU SWITCH INTO. It is the Xano `projects` row (Settings › Workspace › Projects): a named
// group of websites with people on it. Choosing one in the switcher narrows every list of "all sites" to its
// websites; "All sites" narrows nothing; "Not in a project" is every website no project of yours holds (your own,
// unfiled ones). The choice is one of:
//
//   null      All sites. The default, and what an unknown or deleted project falls back to.
//   number    One project, by its id.
//   "none"    Not in a project.
//
// MEMBERSHIP IS BY XANO SITE ID. A project holds `project_sites` rows naming `sites.id`. A website from GET
// /v1/websites carries that id as `xano_site_id` (a Xano site) or `website_id` (a website built in Bloxx, once the
// registry has its Xano row), the same id the Analytics summary keys websites by (features/analytics-home/movers.ts
// summaryIdOf). Nothing here looks at a website's kind, so any kind the registry adds (an app) is scoped the same way
// as soon as it has that id.
//
// THE DATA WORKSPACE (Tables, Interfaces, Studio: lib/scope.tsx) IS A DIFFERENT THING and stays its own choice. A
// project can PIN one (`data_workspace_id`, a column the xano/project-workspaces kit adds): picking that project then
// also picks its data workspace. A project with no pin leaves the data workspace as it was, and so does All sites.

export type ProjectChoice = null | number | "none";

/** The address's say: `?project=<id>`, `?project=none`, `?project=all`. Absent: the address says nothing. */
export const PROJECT_PARAM = "project";
/** Where the choice is remembered between visits (as the data workspace and the site are, lib/scope.tsx). */
export const PROJECT_KEY = "bloxx_project_scope";

/** What the address or storage says. `undefined`: it says nothing usable (absent, empty, or not a choice). */
export function parseProjectChoice(raw: string | null | undefined): ProjectChoice | undefined {
  if (raw == null) return undefined;
  const v = raw.trim().toLowerCase();
  if (v === "all") return null;
  if (v === "none") return "none";
  return /^[1-9]\d{0,9}$/.test(v) ? Number(v) : undefined;
}

/** The choice as the address and storage write it. */
export const formatProjectChoice = (c: ProjectChoice): string => (c === null ? "all" : String(c));

/** A project as the switcher reads it: GET admin/projects, plus the two fields the kit adds (both optional). */
export interface ProjectRef {
  id: number;
  name: string;
  site_count?: number;
  /** The project's Xano site ids, when the list carries them (xano/project-workspaces). Absent: read each project. */
  site_ids?: number[] | null;
  /** The data workspace this project pins, when the server says (xano/project-workspaces). */
  data_workspace_id?: string | null;
}

/** project id → its Xano site ids. Built from the list's `site_ids`, or each project's own read (GET admin/project). */
export type Membership = ReadonlyMap<number, readonly number[]>;

/** The Xano site id a website is a member of a project by: its own, or a built website's registry id. */
export function websiteSiteId(w: { xano_site_id?: number | null; website_id?: number | null }): number | null {
  return w.xano_site_id ?? w.website_id ?? null;
}

/** Membership from the list alone, for the projects whose rows carry `site_ids`. */
export function membershipFromList(projects: readonly ProjectRef[]): Map<number, readonly number[]> {
  const m = new Map<number, readonly number[]>();
  for (const p of projects) if (Array.isArray(p.site_ids)) m.set(p.id, p.site_ids);
  return m;
}

/** The projects whose membership still has to be read (one GET admin/project each) for this choice. */
export function projectsToRead(choice: ProjectChoice, projects: readonly ProjectRef[], wantAll: boolean): number[] {
  const known = membershipFromList(projects);
  const need = choice === "none" || wantAll ? projects.map((p) => p.id) : typeof choice === "number" ? [choice] : [];
  return need.filter((id) => !known.has(id) && projects.some((p) => p.id === id));
}

/** What narrows a list. `null` from projectFilter means nothing does (All sites). */
export interface ProjectFilter {
  kind: "project" | "none";
  /** "project": the project's site ids. "none": every site id any project holds. */
  ids: ReadonlySet<number>;
}

/**
 * The filter for a choice. `null`: All sites, nothing narrows. `undefined`: not known yet (a membership still being
 * read), so a list should wait rather than show everything and then shrink.
 */
export function projectFilter(choice: ProjectChoice, projects: readonly ProjectRef[], membership: Membership): ProjectFilter | null | undefined {
  if (choice === null) return null;
  if (choice === "none") {
    const ids = new Set<number>();
    for (const p of projects) {
      const m = membership.get(p.id);
      if (!m) return undefined;
      for (const id of m) ids.add(id);
    }
    return { kind: "none", ids };
  }
  const m = membership.get(choice);
  return m ? { kind: "project", ids: new Set(m) } : undefined;
}

/** Whether a Xano site id is inside the filter. A site with no id is in no project, so it is "Not in a project". */
export function inProjectFilter(f: ProjectFilter | null | undefined, siteId: number | null | undefined): boolean {
  if (!f) return true;
  const has = siteId != null && f.ids.has(siteId);
  return f.kind === "project" ? has : !has;
}

/** The websites a list shows for the filter (in their own order). */
export function filterWebsites<T extends { xano_site_id?: number | null; website_id?: number | null }>(list: readonly T[], f: ProjectFilter | null | undefined): T[] {
  return f ? list.filter((w) => inProjectFilter(f, websiteSiteId(w))) : [...list];
}

/** Rows keyed by a Xano site id (pages, keywords, leads, connection checks) for the filter. */
export function filterBySiteId<T>(rows: readonly T[], f: ProjectFilter | null | undefined, idOf: (row: T) => number | null | undefined): T[] {
  return f ? rows.filter((r) => inProjectFilter(f, idOf(r))) : [...rows];
}

/** How many websites each project holds, and how many no project holds, from the websites you can open. */
export function projectCounts(websites: readonly { xano_site_id?: number | null; website_id?: number | null }[], projects: readonly ProjectRef[], membership: Membership): { byProject: Map<number, number>; none: number | null } {
  const byProject = new Map<number, number>();
  const everywhere = new Set<number>();
  let complete = true;
  for (const p of projects) {
    const m = membership.get(p.id);
    if (!m) { complete = false; continue; }
    const ids = new Set(m);
    for (const id of m) everywhere.add(id);
    byProject.set(p.id, websites.filter((w) => { const id = websiteSiteId(w); return id != null && ids.has(id); }).length);
  }
  const none = complete ? websites.filter((w) => { const id = websiteSiteId(w); return id == null || !everywhere.has(id); }).length : null;
  return { byProject, none };
}

/**
 * The choice to keep once the project list has loaded: a project that is no longer yours (deleted, or you were taken
 * off it) falls back to All sites, never to an empty screen. "Not in a project" with no projects at all is All sites.
 */
export function settleChoice(choice: ProjectChoice, projects: readonly ProjectRef[]): ProjectChoice {
  if (choice === null) return null;
  if (choice === "none") return projects.length ? "none" : null;
  return projects.some((p) => p.id === choice) ? choice : null;
}

/**
 * THE ADDRESS WINS (as it does for the site, components/shell/Shell.tsx): when the address names a website that is not
 * in the chosen project, the project falls back to All sites rather than the switcher claiming a project the screen is
 * not showing. Only once the filter is known; a website still loading never moves it.
 */
export function choiceForAddress(choice: ProjectChoice, f: ProjectFilter | null | undefined, site: { xano_site_id?: number | null; website_id?: number | null } | null | undefined): ProjectChoice {
  if (choice === null || !site || f === undefined) return choice;
  return inProjectFilter(f, websiteSiteId(site)) ? choice : null;
}

/** The data workspace to switch to when this choice is made, or undefined to leave the data workspace alone. */
export function pinnedWorkspace(choice: ProjectChoice, projects: readonly ProjectRef[]): string | undefined {
  if (typeof choice !== "number") return undefined;
  return projects.find((p) => p.id === choice)?.data_workspace_id || undefined;
}

/** The switcher's and the chip's name for the choice. */
export function choiceLabel(choice: ProjectChoice, projects: readonly ProjectRef[]): string {
  if (choice === null) return "All projects";
  if (choice === "none") return "Not in a project";
  return projects.find((p) => p.id === choice)?.name ?? "Project";
}

/** The choice's websites in a sentence: "Admiral Business's sites", "the sites not in a project", "every site". */
export function choiceSites(choice: ProjectChoice, projects: readonly ProjectRef[]): string {
  if (choice === null) return "every site";
  if (choice === "none") return "the sites not in a project";
  return `${projects.find((p) => p.id === choice)?.name ?? "this project"}'s sites`;
}

/** An address with the choice written into it (`?project=`), All sites removing it. Keeps every other parameter. */
export function withProjectParam(href: string, choice: ProjectChoice): string {
  const [rest, hash = ""] = href.split("#");
  const [path, qs = ""] = rest!.split("?");
  const q = new URLSearchParams(qs);
  if (choice === null) q.delete(PROJECT_PARAM);
  else q.set(PROJECT_PARAM, formatProjectChoice(choice));
  const s = q.toString();
  return `${path}${s ? `?${s}` : ""}${hash ? `#${hash}` : ""}`;
}

/**
 * What a project's page calls the websites in it. Sites today; the registry is about to add apps (another kind of
 * website), and a project holding one says so, without this page having to learn each kind.
 */
export function projectItemsHeading(kinds: readonly string[]): string {
  return kinds.some((k) => k === "app") ? "Sites and apps" : "Sites";
}

/** One website's kind as a short label: an app says so; every other kind is a site. */
export const websiteKindLabel = (kind: string | null | undefined): string => (kind === "app" ? "App" : "Site");
